import { CellContext } from "@/lib/Components/DataTable/types";
import {
  RelocationInclusionType,
  RelocationListQueryVariables,
  RelocationStatus,
  RelocationType,
} from "@/gql/graphql";
import {
  getRelocationTableVariables,
  RelocationColumnId,
  relocationTableDefaultSorting,
} from "@/app/Dashboard/Utils/getRelocationTableVariables";
import {
  formatCurrency,
  formatDistance,
} from "@/lib/Formatters/formatCurrency";
import { ReactNode, useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslations } from "use-intl";
import { RelocationStatusCell } from "@/app/Relocations/Components/RelocationStatusCell";
import { formatDate } from "@/lib/Formatters/formatDate";
import { QuantityBar } from "@/app/Relocations/Components/QuantityBar";
import dayjs from "dayjs";
import {
  RelocationListItem,
  relocationListQuery,
} from "@/app/Relocations/GraphQL/relocationListQuery";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
} from "@/lib/Components/DataTable/DataTable";
import { useGlobalSupplier } from "@/app/Suppliers/Utils/useGlobalSupplier";
import {
  MultiSelectFilter,
  MultiSelectFilterOption,
} from "@/lib/Components/DataTable/Filters/MultiSelectFilter";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import { useRelocationActions } from "@/app/Relocations/Hooks/useRelocationActions";
import type { TableId } from "@/app/Common/Utils/table-id";

import { Button } from "@/components/catalyst/button";
import { useConfirmDeleteDialog } from "@/lib/Components/Dialog/Hooks/useConfirmDeleteDialog";
import { Checkbox } from "@/components/catalyst/checkbox";
import { toast } from "sonner";
import { RegionFilter } from "@/app/Common/Components/RegionFilter";
import { CityFilter } from "@/lib/Components/DataTable/Filters/CityFilter";
import { SupplierFilter } from "@/lib/Components/DataTable/Filters/SupplierFilter";
import { BadgeButton } from "@/components/catalyst/badge";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { RelocationAdditionalDestinationDialog } from "@/app/Relocations/Components/Dialogs/RelocationAdditionalDestinationDialog";
import { updateRelocationStatuses } from "@/app/Relocations/GraphQL/relocationMutations";
import { RelocationReferenceCell } from "@/app/Relocations/Components/Cells/RelocationReferenceCell";
import { RelocationGraphic } from "@/app/Relocations/Components/RelocationGraphic";
import { Link } from "@/components/catalyst/link";
import { Highlighter } from "@/lib/Components/Highlighter";
import { inclusionIconMap } from "@/app/Relocations/Maps/inclusionIconMap";
import { dealScoreTone } from "@/app/Relocations/Utils/dealScoreTone";
import { RelocationDealChatDialog } from "@/app/Relocations/Components/RelocationDealChatDialog";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ArchiveBoxArrowDownIcon,
  Cog6ToothIcon,
} from "@heroicons/react/20/solid";
import { Star, TriangleAlert } from "lucide-react";
import {
  RelocationTableState,
  defaultRelocationTableState,
} from "@/app/Relocations/Utils/relocationTableState";

type RelocationColumns = RelocationColumnId;

export function getInclusionIconColor(type: RelocationInclusionType): string {
  switch (type) {
    case RelocationInclusionType.Fuel:
      return "text-green-600";
    case RelocationInclusionType.Ferry:
      return "text-blue-600";
    default:
      return "text-yellow-500";
  }
}

function getStatusOptions(
  t: (key: string) => string,
): MultiSelectFilterOption<RelocationStatus>[] {
  return [
    {
      label: t("statusLabels.READY"),
      value: RelocationStatus.Ready,
    },
    {
      label: t("statusLabels.SOLD_OUT"),
      value: RelocationStatus.SoldOut,
    },
    {
      label: t("statusLabels.PAUSED"),
      value: RelocationStatus.Paused,
    },
    {
      label: t("statusLabels.EXPIRED"),
      value: RelocationStatus.Expired,
    },
    {
      label: t("statusLabels.ARCHIVED"),
      value: RelocationStatus.Archived,
    },
    {
      label: t("statusLabels.DRAFT"),
      value: RelocationStatus.Draft,
    },
  ];
}

type DashboardRelocationsTableProps = {
  title?: string;
  rightButtons?: ReactNode;
  id: TableId;
  queryVariables?: Partial<RelocationListQueryVariables>;
  hiddenColumns?: RelocationColumns[];
  searchable?: boolean;
  type?: RelocationType[];
  supplierId?: string;

  //Un-controlled component
  defaultState?: Partial<RelocationTableState>;

  //Controlled component
  state?: RelocationTableState;
  onStateChange?: (state: RelocationTableState) => void;
};

export function RelocationTable({
  title,
  rightButtons,
  id,
  defaultState,
  queryVariables,
  hiddenColumns,
  state: stateProp,
  onStateChange,
  searchable,
  type,
  supplierId: supplierIdProp,
}: DashboardRelocationsTableProps) {
  "use no memo";
  const { supplierId: globalSupplierId } = useGlobalSupplier();
  const getActions = useRelocationActions();
  const { open } = useConfirmDeleteDialog();
  const { mutateAsync } = useGqlMutation(updateRelocationStatuses);

  const [localState, setLocalState] = useState<RelocationTableState>({
    ...defaultRelocationTableState,
    ...defaultState,
  });

  const state = stateProp ?? localState;

  // Any change other than paging puts the user back on the first page.
  const updateState = (patch: Partial<RelocationTableState>) => {
    const next = { ...state, page: 1, ...patch };

    if (onStateChange) {
      onStateChange(next);
    } else {
      setLocalState(next);
    }
  };

  const {
    statuses: status,
    regions,
    departureCityIds,
    deliveryCityIds,
  } = state;

  const scopedSupplierId = supplierIdProp ?? globalSupplierId;
  const supplierId = scopedSupplierId ?? state.supplierId;
  const t = useTranslations("relocation");
  const tTable = useTranslations("relocation.table");

  const columns: DataTableColDef<
    RelocationListItem,
    unknown,
    RelocationColumns
  >[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            table.getIsSomePageRowsSelected()
          }
          color="indigo"
          onChange={(value) => table.toggleAllPageRowsSelected(value)}
          aria-label={tTable("selectAll")}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          color="indigo"
          disabled={row.original.status === RelocationStatus.Archived}
          checked={row.getIsSelected()}
          onChange={(value) => row.toggleSelected(value)}
          aria-label={tTable("selectRow")}
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "status",
      cell: RelocationStatusCell,
      accessorFn: (model) => model,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("status")} />
      ),
    },
    {
      id: "relocation",
      accessorFn: (model) => model,
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("relocation")} />
      ),
      cell: RelocationCell,
    },
    {
      id: "deal_score",
      accessorFn: (model) => model.deal_score,
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("dealScore")} />
      ),
      cell: DealScoreCell,
    },
    {
      id: "vehicle",
      accessorFn: (model) => model.vehicle.name,
      enableSorting: true,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("vehicle")} />
      ),
      cell: VehicleCell,
    },
    {
      id: "line",
      header: tTable("plate"),
      cell: RelocationReferenceCell,
      accessorFn: (model) => model,
    },
    {
      id: "deal",
      header: tTable("deal"),
      accessorFn: (model) => model,
      cell: DealCell,
    },
    {
      id: "bookings",
      header: tTable("bookings"),
      accessorFn: (model) => model.count,
      cell: QuantityCell,
    },
    {
      id: "expires_at",
      accessorFn: (model) => model,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("expires")} />
      ),
      cell: ExpiresCell,
    },
    {
      id: "created_at",
      accessorFn: (model) => model,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={tTable("created")} />
      ),
      cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
    },
  ];

  return (
    <DataTable
      id={id}
      searchable={searchable}
      page={state.page}
      onPageChange={(page) => updateState({ page })}
      search={state.search}
      onSearchChange={(search) => updateState({ search })}
      hiddenColumns={hiddenColumns}
      getActions={getActions}
      initialSorting={relocationTableDefaultSorting}
      filters={
        <div className="flex flex-wrap gap-2">
          <MultiSelectFilter
            label={tTable("status")}
            options={getStatusOptions(t)}
            selected={status}
            onChange={(statuses) => updateState({ statuses })}
          />

          {scopedSupplierId ? null : (
            <SupplierFilter
              label={tTable("supplier")}
              selected={state.supplierId}
              onChange={(supplierId) => updateState({ supplierId })}
            />
          )}

          <RegionFilter
            selected={regions}
            onChange={(regions) => updateState({ regions })}
          />

          <CityFilter
            placeholder={tTable("departureCity")}
            selected={departureCityIds}
            regions={regions}
            onChange={(departureCityIds) => updateState({ departureCityIds })}
          />

          <CityFilter
            placeholder={tTable("deliveryCity")}
            selected={deliveryCityIds}
            regions={regions}
            onChange={(deliveryCityIds) => updateState({ deliveryCityIds })}
          />
        </div>
      }
      title={title}
      rightButtons={(table) => {
        const selected = table.getSelectedRowModel().rows;

        return selected.length > 0 ? (
          <div className="flex h-9 items-center space-x-3 rounded-lg border border-blue-200 bg-blue-50 pl-4 shadow-sm">
            <div className="text-sm font-medium text-blue-700">
              {tTable("selected", { count: selected.length })}
            </div>

            <Button
              plain
              className="text-sm text-blue-600 underline decoration-dashed underline-offset-2 hover:text-blue-800"
              onClick={() => {
                table.toggleAllPageRowsSelected(false);
              }}
            >
              {tTable("deselectAll")}
            </Button>

            <Button
              onClick={() => {
                open({
                  title: tTable("archiveTitle"),
                  message: tTable("archiveMessage"),
                  buttonText: tTable("archiveButton"),
                  onDelete: async () => {
                    await mutateAsync({
                      ids: selected.map((row) => row.original.id),
                      status: RelocationStatus.Archived,
                    });

                    table.toggleAllPageRowsSelected(false);

                    toast.success(
                      tTable("archiveSuccess", { count: selected.length }),
                    );
                  },
                });
              }}
            >
              <ArchiveBoxArrowDownIcon />
              {tTable("archive")}
            </Button>
          </div>
        ) : (
          rightButtons
        );
      }}
      columns={columns}
      emptyState={<RelocationEmptyState />}
      document={relocationListQuery}
      accessor={(data) => data.relocations}
      getQueryVariables={getRelocationTableVariables({
        queryVariables,
        status,
        type,
        regions,
        departureCityIds,
        deliveryCityIds,
        supplierId,
      })}
    />
  );
}

function RelocationEmptyState() {
  const t = useTranslations("relocation.relocationEmpty");

  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-4 py-6 text-center">
      <div className="w-full max-w-sm">
        <RelocationGraphic />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">{t("title")}</p>
        <p className="mt-1 text-xs text-gray-500">{t("subtitle")}</p>
      </div>
    </div>
  );
}

export function VehicleCell({
  row,
  table,
}: CellContext<RelocationListItem, any>) {
  const search = table.options.meta?.search ?? "";
  const t = useTranslations("vehicle.transmission");

  return (
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-gray-900">
        <Highlighter searchString={search}>
          {row.original.vehicle.name}
        </Highlighter>
      </p>
      <div className="flex items-center space-x-1">
        <Cog6ToothIcon className="h-3 w-3 text-gray-400" />
        <span className="truncate text-xs text-gray-500">
          {t(row.original.vehicle.transmission)}
        </span>
      </div>
    </div>
  );
}

export function QuantityCell({ row }: CellContext<RelocationListItem, any>) {
  const t = useTranslations("relocation.table");
  const booked = row.original.bookedBookingsCount ?? 0;
  const total = row.original.count + booked;

  if (total === 0) {
    return (
      <div className="text-center">
        <span className="text-xs text-gray-400">{t("none")}</span>
      </div>
    );
  }

  return <QuantityBar booked={booked} total={total} />;
}

function RelocationCell({ table, row }: CellContext<RelocationListItem, any>) {
  "use no memo";
  const { hasSupplier } = useGlobalSupplier();
  const { open } = useDialog(RelocationAdditionalDestinationDialog);
  const t = useTranslations("relocation.table");
  const search = table.options.meta?.search ?? "";
  const relocation = row.original;

  const imageSource = relocation.ogImage ?? relocation.vehicle.heroImage;

  return (
    <div className="flex min-w-0 space-x-3">
      {imageSource ? (
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg">
          {relocation.ogImage ? (
            <ImoovaImage
              alt={relocation.reference}
              media={relocation.ogImage}
              className="h-full w-full object-cover"
            />
          ) : (
            <img
              alt={relocation.vehicle.name}
              src={relocation.vehicle.heroImage}
              className="h-full w-full object-contain"
            />
          )}
        </div>
      ) : null}

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-1.5">
          <Link
            className="block truncate font-medium text-blue-600 hover:text-blue-800"
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
            params={(prev) => ({
              ...prev,
              relocationId: relocation.id,
            })}
            viewTransition
          >
            <Highlighter searchString={search}>
              {relocation.reference}
            </Highlighter>
          </Link>
          {relocation.priority !== null &&
            relocation.priority !== undefined && (
              <Star className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400" />
            )}
        </div>

        <div className="flex min-w-0 items-center space-x-1 text-sm">
          <span className="truncate font-medium text-gray-900">
            <Highlighter searchString={search}>
              {relocation.departureOffice.name}
            </Highlighter>
          </span>
          <ArrowRightIcon className="h-3 w-3 shrink-0 text-gray-400" />
          <span className="truncate font-medium text-gray-900">
            <Highlighter searchString={search}>
              {relocation.deliveryOffice.name}
            </Highlighter>
          </span>
          {relocation.linkedRelocationCount > 0 ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <BadgeButton
                  className="bg-blue-50 text-xs text-blue-700 hover:bg-blue-100"
                  onClick={() => {
                    open({
                      relocationId: relocation.id,
                    });
                  }}
                >
                  +{relocation.linkedRelocationCount}
                </BadgeButton>
              </TooltipTrigger>
              <TooltipContent>
                <div className="space-y-0.5">
                  {relocation.group.relocations
                    .filter(
                      (r) =>
                        r.id !== relocation.id &&
                        r.status !== RelocationStatus.Archived,
                    )
                    .map((r) => (
                      <p key={r.id}>
                        <span className="text-gray-400">{t("or")}</span>{" "}
                        {r.deliveryOffice.name}
                      </p>
                    ))}
                </div>
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>

        {!hasSupplier && (
          <p className="truncate text-xs text-gray-600">
            <Highlighter searchString={search}>
              {relocation.supplier.name}
            </Highlighter>
          </p>
        )}

        <p className="text-xs text-gray-500">
          {formatDate(relocation.available_from_date)} -{" "}
          {formatDate(relocation.available_to_date)}
        </p>

        {relocation.status === RelocationStatus.Ready &&
        relocation.departure_dates.length < 3 ? (
          <div className="flex items-center text-[11px] text-amber-600">
            <TriangleAlert className="mr-0.5 inline h-3 w-3" />
            <span>
              {t("departureDatesRemaining", {
                count: relocation.departure_dates.length,
              })}
            </span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function DealCell({ row }: CellContext<RelocationListItem, any>) {
  const relocation = row.original;
  const t = useTranslations("relocation");
  const tTable = useTranslations("relocation.table");

  return (
    <div className="space-y-1.5">
      <div className="text-xs">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help border-b border-dashed border-gray-400">
              <span className="font-medium text-gray-900">
                {relocation.hire_units_allowed}
              </span>
              <span className="text-gray-400">+</span>
              <span className="font-medium text-gray-600">
                {relocation.extra_hire_units_allowed}
              </span>
              <span className="ml-1 text-gray-500">
                {t(`hire_unit_type_plural.${relocation.hire_unit_type}`)}
              </span>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-0.5 text-xs">
              <div className="flex justify-between gap-4">
                <span>{tTable("rate")}</span>
                <span className="font-medium">
                  {formatCurrency(
                    relocation.hire_unit_rate,
                    relocation.currency,
                  )}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span>{tTable("extra")}</span>
                <span className="font-medium">
                  {formatCurrency(
                    relocation.extra_hire_unit_rate,
                    relocation.currency,
                  )}
                </span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      <div className="text-xs">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="cursor-help border-b border-dashed border-gray-400 font-medium text-gray-900">
              {formatDistance(
                relocation.distance_allowed,
                relocation.measurement,
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <div className="space-y-0.5 text-xs">
              <div className="flex justify-between gap-4">
                <span>
                  {tTable("perExtra", {
                    unit: t(`hire_unit_type.${relocation.hire_unit_type}`),
                  })}
                </span>
                <span className="font-medium">
                  {formatDistance(
                    relocation.extra_distance_allowed_per_extra_hire_unit ?? 0,
                    relocation.measurement,
                  )}
                </span>
              </div>
              <div className="flex justify-between gap-4">
                <span>{tTable("excessRate")}</span>
                <span className="font-medium">
                  {formatCurrency(
                    relocation.excess_distance_rate ?? 0,
                    relocation.currency,
                  )}
                  /{t(`distanceUnit.${relocation.measurement}`)}
                </span>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>

      {relocation.inclusions.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {relocation.inclusions.map(
            ({ id, type, value, has_free_tank, description }) => {
              const Icon = inclusionIconMap[type];

              let tooltipContent = "";
              if (has_free_tank) {
                tooltipContent = t("summaryCard.freeTank");
              } else {
                if (value) {
                  tooltipContent = formatCurrency(value, relocation.currency);
                }
                if (description) {
                  tooltipContent +=
                    (tooltipContent ? " - " : "") +
                    description.replace(/<[^>]*>/g, "");
                }
              }

              return (
                <Tooltip key={id}>
                  <TooltipTrigger asChild>
                    <div className="cursor-help">
                      <Icon
                        className={cn("h-4 w-4", getInclusionIconColor(type))}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{tooltipContent || "Inclusion"}</p>
                  </TooltipContent>
                </Tooltip>
              );
            },
          )}
        </div>
      ) : null}
    </div>
  );
}

export function DealScoreCell({ row }: CellContext<RelocationListItem, any>) {
  const { open } = useDialog(RelocationDealChatDialog);
  const score = row.original.deal_score;

  return (
    <button
      type="button"
      onClick={() => open({ relocationId: row.original.id })}
      className="rounded-full"
    >
      {score === null || score === undefined ? (
        <span className="text-sm text-gray-400">—</span>
      ) : (
        <span
          className={cn(
            "inline-flex rounded-full px-2 py-0.5 text-xs font-semibold",
            dealScoreTone(score).badge,
          )}
        >
          {score}/10
        </span>
      )}
    </button>
  );
}

export function ExpiresCell({ row }: CellContext<RelocationListItem, any>) {
  const t = useTranslations("relocation.table");
  const relocation = row.original;
  const days = dayjs(relocation.available_to_date).diff(dayjs(), "days");
  const showExpiry = days >= 0 && relocation.status === RelocationStatus.Ready;

  if (!showExpiry) {
    return null;
  }

  return (
    <div
      className={cn("text-right text-sm", {
        "text-red-600": days < 3,
        "text-orange-600": days >= 3 && days < 7,
        "text-gray-500": days >= 7,
      })}
    >
      {days === 0 ? t("today") : t("daysShort", { count: days })}
    </div>
  );
}
